// Thin PixiJS rendering layer. Pixi renders; the engine owns all logic.
// Builds room visuals into the shared world (optionally offset, for edge-pan),
// plus a persistent hero, a fade overlay, a camera, and a debug overlay.
import { Application, Assets, Container, Graphics, Sprite, Text, type Texture } from "pixi.js";
import type { Fx, Room, Shape } from "../core/schema";
import type { Vec2 } from "../core/geometry";
import { createFireFilter, type FireFilter } from "./fireShader";

// 8-way direction name from a movement vector (+x = east, +y = south, screen coords).
function dir8(dx: number, dy: number): string {
  const oct = ((Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) % 8) + 8) % 8;
  return ["east", "south-east", "south", "south-west", "west", "north-west", "north", "north-east"][oct]!;
}

const flatOff = (poly: Vec2[], o: Vec2): number[] => poly.flatMap((p) => [p.x + o.x, p.y + o.y]);
const flat = (poly: Vec2[]): number[] => poly.flatMap((p) => [p.x, p.y]);

// Add one decor shape's path to a Graphics (caller fills). Arc = elliptical wedge closed to centre
// (bowls/arches), sampled to a polygon so it works for any rx≠ry.
function addShape(g: Graphics, s: Shape, o: Vec2): void {
  if (s.type === "poly") g.poly(s.points.flatMap(([x, y]) => [x + o.x, y + o.y]));
  else if (s.type === "rect") g.rect(o.x + s.x, o.y + s.y, s.w, s.h);
  else if (s.type === "ellipse") g.ellipse(o.x + s.cx, o.y + s.cy, s.rx, s.ry);
  else {
    const pts: number[] = [o.x + s.cx, o.y + s.cy];
    const N = 24;
    for (let k = 0; k <= N; k++) {
      const a = s.a0 + ((s.a1 - s.a0) * k) / N;
      pts.push(o.x + s.cx + Math.cos(a) * s.rx, o.y + s.cy + Math.sin(a) * s.ry);
    }
    g.poly(pts);
  }
}

export interface HoverFx {
  kind: string;
  poly: Vec2[] | null;
}
export interface Ripple {
  x: number;
  y: number;
  age: number;
}
/** The armed-target prompt: a context glyph floating over an interactable. */
export interface Prompt {
  x: number;
  y: number;
  verb: string;
  poly: Vec2[] | null; // hotspot outline (null for npc -> ring drawn at feet)
  feet: Vec2 | null; // npc feet for the ring
}
/** A wordless speech bubble: a row of pictographic glyphs above a speaker. */
export interface Dialog {
  x: number; // speaker head, room-space
  y: number;
  glyphs: string[];
  mood: string;
  more: boolean; // is there another line after this one?
}

const MOOD_TINT: Record<string, number> = {
  neutral: 0xe8dcc0,
  warm: 0xffcf9e,
  wary: 0xc9b3e0,
  sad: 0x9fb6d6,
  glad: 0xb6d6a0,
};

/** Draw a placeholder figure with feet at local (0,0). Hero carries the flame.
 * Sized for MI1-style framing (~270-unit-tall rooms) so the character reads large (≈50 units
 * tall ≈ 1/5 of the screen, like Guybrush). Scale all metrics through FIG. */
const FIG = 1.8;
function drawFigure(container: Container, bodyColor: number, withFlame: boolean): void {
  container.removeChildren();
  const g = new Graphics();
  g.ellipse(0, 0, 7 * FIG, 3 * FIG).fill({ color: 0x000000, alpha: 0.35 }); // shadow
  g.roundRect(-5 * FIG, -22 * FIG, 10 * FIG, 20 * FIG, 3 * FIG).fill({ color: bodyColor }); // body
  g.circle(0, -26 * FIG, 4 * FIG).fill({ color: 0xe8d3b0 }); // head
  if (withFlame) {
    g.circle(7 * FIG, -14 * FIG, 2.2 * FIG).fill({ color: 0xffb347 });
    g.circle(7 * FIG, -15 * FIG, 1.1 * FIG).fill({ color: 0xfff1c1 });
  }
  container.addChild(g);
}

export interface RoomHandle {
  room: Room;
  offset: Vec2;
  bg: Graphics;
  decor: Graphics;
  bgSprite: Sprite | null; // painted backdrop (art-swap), if room.bgImage is set
  occluderNodes: Map<string, Container>;
  npcNodes: Map<string, Container>;
  gatedAreaNodes: Map<string, Graphics>; // floor fill shown only when the area is "open"
  fireUnits: FireUnit[]; // shader-animated painted fire (one per "flames" fx), filled once art loads
}

/** A shader-animated fire: the fire-cutout sprite (art with a fire-shaped alpha) + its twinkle filter. */
export interface FireUnit {
  sprite: Sprite;
  filter: FireFilter | null; // set once the cutout texture loads
}

export interface DebugInfo {
  path: Vec2[];
  target: Vec2 | null;
  clamped: boolean;
  lastClick: Vec2 | null;
}

export class Renderer {
  app = new Application();
  world = new Container();
  hero = new Container();
  private heroSprite = new Sprite();
  private heroIdle = new Map<string, Texture>();
  private heroWalk = new Map<string, Texture[]>();
  private heroFallback: Container | null = null;
  private heroReady = false;
  private heroDir = "south";
  private debug = new Graphics();
  // living-scene ambient effects: one layer behind actors, one in front (both in world-space)
  private ambientBack = new Graphics();
  private ambientFront = new Graphics();
  private fx = new Graphics();
  private prompt = new Graphics();
  private dialog = new Graphics();
  private light = new Graphics();
  private fade = new Graphics();
  // text-first caption (prose dialogue / examine lines / room cards) — screen-space, bottom-centre
  private captionBg = new Graphics();
  private caption = new Text({
    text: "",
    style: { fontFamily: "Georgia, 'Times New Roman', serif", fontSize: 22, fill: 0xf0e6d0, align: "center", wordWrap: true, wordWrapWidth: 800, lineHeight: 28, dropShadow: { color: 0x000000, alpha: 0.8, blur: 2, distance: 1, angle: Math.PI / 2 } },
  });
  scale = 3;
  viewW = 0; // viewport size in screen px (= backing buffer = window)
  viewH = 0;
  private designH = 270; // room-units of vertical framing; scale fits this to the window height
  // current room size + letterbox centring offsets (so single-screen rooms narrower/shorter than the
  // window are CENTRED, not left/top-aligned — symmetric bars instead of one-sided)
  private roomW = 480;
  private roomH = 270;
  private offX = 0;
  private offY = 0;

  private showDebug = true;

  /** Initialize TRUE FULL-SCREEN. The backing buffer tracks the window (resizeTo) so the canvas
   * fills the screen with no letterbox on any monitor aspect. `scale` is derived so the design
   * view HEIGHT (`view.h`) always fills the window height — vertical fills edge-to-edge; rooms
   * wider than the resulting view scroll horizontally (classic SCUMM framing). Pixel art stays
   * crisp (resolution 1 + CSS image-rendering:pixelated). `view`/`scale` args are the headless
   * fallback (no window). */
  async init(view: { w: number; h: number }, scale: number, bgColor: number): Promise<void> {
    this.designH = view.h;
    const hasWindow = typeof window !== "undefined";
    await this.app.init({
      ...(hasWindow ? { resizeTo: window } : { width: view.w * scale, height: view.h * scale }),
      background: bgColor,
      antialias: false,
      roundPixels: true,
      preference: "webgl", // GLSL fire filter (fireShader) needs the WebGL backend
    });
    this.applyViewport();
    // keep viewport (scale + camera math + fade overlay) in sync as the window resizes
    this.app.renderer.on("resize", () => this.applyViewport());
    document.getElementById("app")!.appendChild(this.app.canvas);

    this.world.sortableChildren = true;
    this.app.stage.addChild(this.world);

    // the carried flame's glow sits above the floor but below actors (light falls on the floor)
    this.light.zIndex = -400_000;
    this.world.addChild(this.light);

    // ambient effects: back layer above the backdrop/decor but below actors; front layer above
    // actors but below the hover/prompt/debug overlays (embers & motes drift over everything)
    this.ambientBack.zIndex = -500_000;
    this.world.addChild(this.ambientBack);
    this.ambientFront.zIndex = 700_000;
    this.world.addChild(this.ambientFront);

    drawFigure(this.hero, 0x6b5b95, true);
    this.heroFallback = this.hero.children[0] ?? null; // greybox figure, shown until sprites load
    this.heroSprite.anchor.set(0.5, 1);
    this.heroSprite.visible = false;
    this.hero.addChild(this.heroSprite);
    this.world.addChild(this.hero);

    this.fx.zIndex = 900_000; // hover/click feedback, above content, below debug
    this.world.addChild(this.fx);
    this.prompt.zIndex = 905_000; // armed-target glyph, above hover fx
    this.world.addChild(this.prompt);
    this.dialog.zIndex = 906_000; // speech bubble, above the prompt
    this.world.addChild(this.dialog);
    this.debug.zIndex = 1_000_000;
    this.world.addChild(this.debug);

    // caption sits above the world but below the fade (so transitions cover it)
    this.caption.anchor.set(0.5, 1);
    this.caption.visible = false;
    this.app.stage.addChild(this.captionBg);
    this.app.stage.addChild(this.caption);
    this.layoutCaption();

    this.app.stage.addChild(this.fade); // screen-space overlay
  }

  /** Recompute the viewport from the current window: buffer = window, and world scale fits the
   * design height to the window height (so it fills vertically; wide rooms then scroll). */
  private applyViewport(): void {
    this.viewW = this.app.screen.width;
    this.viewH = this.app.screen.height;
    this.scale = this.viewH / this.designH;
    this.world.scale.set(this.scale);
    // centre the room when it's smaller than the window (symmetric letterbox); 0 when it fills/scrolls
    this.offX = Math.max(0, Math.round((this.viewW - this.roomW * this.scale) / 2));
    this.offY = Math.max(0, Math.round((this.viewH - this.roomH * this.scale) / 2));
    this.layoutCaption();
  }
  /** Tell the renderer the current room's size (drives letterbox centring). */
  setRoomSize(w: number, h: number): void {
    this.roomW = w;
    this.roomH = h;
    this.applyViewport();
  }

  /** Position + wrap the caption at screen bottom-centre for the current viewport. */
  private layoutCaption(): void {
    this.caption.style.wordWrapWidth = Math.min(900, this.viewW * 0.86);
    this.caption.x = this.viewW / 2;
    this.caption.y = this.viewH - 26;
    if (this.caption.visible) this.drawCaptionBg();
  }
  private drawCaptionBg(): void {
    const b = this.caption.getLocalBounds();
    const w = this.caption.width + 36;
    const h = this.caption.height + 22;
    const x = this.viewW / 2 - w / 2;
    const y = this.caption.y - this.caption.height - 11;
    this.captionBg.clear();
    this.captionBg.roundRect(x, y, w, h, 8).fill({ color: 0x0a0a0e, alpha: 0.66 });
    void b;
  }

  /** Show a line of prose (dialogue / examine / room card), or hide it (null). */
  setCaption(text: string | null, color = 0xf0e6d0): void {
    if (!text) {
      this.caption.visible = false;
      this.captionBg.visible = false;
      return;
    }
    this.caption.text = text;
    this.caption.style.fill = color;
    this.caption.visible = true;
    this.captionBg.visible = true;
    this.layoutCaption();
  }

  setCursor(css: string): void {
    this.app.canvas.style.cursor = css;
  }

  /** Click ripples (hover feedback is the cursor change — NO hard polygon outline, which reads as a
   * distracting rectangle, especially over the fire). */
  drawFx(hover: HoverFx, ripples: Ripple[], timeMs: number): void {
    const f = this.fx;
    f.clear();
    void hover;
    for (const r of ripples) {
      const k = r.age / 0.45;
      if (k >= 1) continue;
      f.circle(r.x, r.y, 4 + k * 22).stroke({ width: 1.5, color: 0xffffff, alpha: (1 - k) * 0.7 });
    }
  }

  /** Armed-target prompt: persistent highlight + a context glyph that bobs above it. */
  drawPrompt(p: Prompt | null, timeMs: number): void {
    const g = this.prompt;
    g.clear();
    if (!p) return;
    const warm = 0xffe082;
    const a = 0.55 + 0.3 * Math.sin(timeMs / 260);
    // affordance = the bobbing glyph only (NO polygon outline — a hard rectangle around a hotspot
    // distracts, especially over the fire). NPCs still get a soft feet-ring.
    if (p.feet) g.ellipse(p.feet.x, p.feet.y, 11, 4).stroke({ width: 1.5, color: warm, alpha: a });
    // glyph bobs above the object
    const bob = Math.sin(timeMs / 300) * 1.5;
    const gx = p.x;
    const gy = p.y - 10 + bob;
    this.drawVerbGlyph(g, gx, gy, p.verb, a);
  }

  /** A tiny wordless verb icon (no fonts — stays pixel-crisp + deterministic). */
  private drawVerbGlyph(g: Graphics, x: number, y: number, verb: string, alpha: number): void {
    const warm = 0xfff1c1;
    const back = 0x2a2410;
    if (verb === "talk") {
      // speech bubble with a nub
      g.roundRect(x - 5, y - 5, 10, 7, 2).fill({ color: warm, alpha });
      g.poly([x - 1, y + 2, x + 2, y + 2, x - 1, y + 5]).fill({ color: warm, alpha });
      g.circle(x - 2, y - 1.5, 0.6).fill({ color: back, alpha });
      g.circle(x + 0.5, y - 1.5, 0.6).fill({ color: back, alpha });
      g.circle(x + 3, y - 1.5, 0.6).fill({ color: back, alpha });
    } else if (verb === "kindle") {
      // a little flame (teardrop) — warm to bright at the core
      g.poly([x, y - 6, x + 4, y, x + 3, y + 4, x - 3, y + 4, x - 4, y]).fill({ color: 0xff8a3c, alpha });
      g.poly([x, y - 2, x + 2, y + 1, x, y + 4, x - 2, y + 1]).fill({ color: warm, alpha });
    } else if (verb === "take") {
      // upward chevron (grab)
      g.poly([x, y - 4, x + 4, y + 2, x + 1.5, y + 2, x + 1.5, y + 4, x - 1.5, y + 4, x - 1.5, y + 2, x - 4, y + 2])
        .fill({ color: warm, alpha });
    } else {
      // examine / use: a small diamond with a center dot
      g.poly([x, y - 5, x + 4, y, x, y + 5, x - 4, y]).fill({ color: warm, alpha });
      g.circle(x, y, 1.1).fill({ color: back, alpha });
    }
  }

  /** The carried flame's glow: soft concentric warm rings at (x,y), out to `radius`. A
   * gentle flicker keyed off time. radius<=0 (no flame) draws nothing — darkness. */
  drawLight(x: number, y: number, radius: number, timeMs: number): void {
    const g = this.light;
    g.clear();
    if (radius <= 0) return;
    const flick = 1 + 0.04 * Math.sin(timeMs / 90) + 0.03 * Math.sin(timeMs / 37);
    const r = radius * flick;
    const rings = 5;
    for (let i = rings; i >= 1; i--) {
      const rr = (r * i) / rings;
      const a = 0.06 * (1 - (i - 1) / rings); // brighter toward the centre
      g.circle(x, y, rr).fill({ color: 0xffd9a0, alpha: a });
    }
    g.circle(x, y, Math.max(2, r * 0.12)).fill({ color: 0xfff1c1, alpha: 0.18 }); // hot core
  }

  // ---- living-scene ambient effects (procedural, deterministic, asset-free) ----
  /** Deterministic per-element pseudo-random in [0,1) — NOT Math.random (keeps the look stable). */
  private frand(n: number): number {
    const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
  }

  /** Draw a room's living-scene effects for the current time. Clears + redraws both ambient
   * layers each frame; cosmetic only (never read by the probe/sim). */
  drawAmbient(fx: Fx[] | undefined, timeMs: number): void {
    const back = this.ambientBack;
    const front = this.ambientFront;
    back.clear();
    front.clear();
    if (!fx || !fx.length) return;
    const t = timeMs / 1000;
    for (const e of fx) {
      const g = e.layer === "front" ? front : back;
      switch (e.kind) {
        case "flames": break; // animated by a WebGL shader sprite over the painted fire (spawnFireUnits)
        case "embers": this.fxEmbers(g, e, t); break;
        case "glow": this.fxGlow(g, e, t); break;
        case "shimmer": this.fxShimmer(g, e, t); break;
        case "motes": this.fxMotes(g, e, t); break;
        case "clouds": this.fxClouds(g, e, t); break;
        case "birds": this.fxBirds(g, e, t); break;
      }
    }
  }

  private fxEmbers(g: Graphics, e: Fx, t: number): void {
    for (let i = 0; i < e.count; i++) {
      const ph = this.frand(i + e.seed * 13);
      const speed = 0.05 + 0.08 * this.frand(i + 3);
      const life = (t * speed + ph) % 1; // 0 at the coals -> 1 at the top
      const x = e.x + this.frand(i + 5) * e.w + Math.sin(t * 1.5 + ph * 6.283) * e.w * 0.07;
      const y = e.y + e.h - life * e.h;
      const a = (1 - life) * 0.9;
      const r = (0.6 + this.frand(i + 7) * 0.8) * e.intensity;
      g.circle(x, y, r).fill({ color: life < 0.5 ? 0xffd27a : 0xff8a3c, alpha: a });
    }
  }
  private fxGlow(g: Graphics, e: Fx, t: number): void {
    const cx = e.x + e.w / 2;
    const cy = e.y + e.h / 2;
    const baseR = Math.max(e.w, e.h) / 2;
    const pulse = 1 + 0.06 * Math.sin(t * 2.2 + e.seed) + 0.04 * Math.sin(t * 3.7 + e.seed * 2);
    const R = baseR * pulse * e.intensity;
    const col = e.color ?? 0xffb24a;
    const rings = 5;
    for (let i = rings; i >= 1; i--) {
      const rr = (R * i) / rings;
      const a = 0.055 * (1 - (i - 1) / rings);
      g.ellipse(cx, cy, rr, rr * 0.82).fill({ color: col, alpha: a });
    }
  }
  private fxShimmer(g: Graphics, e: Fx, t: number): void {
    const col = e.color ?? 0x9fdcff;
    for (let i = 0; i < e.count; i++) {
      const ph = this.frand(i + e.seed * 5);
      const y = e.y + this.frand(i + 2) * e.h;
      const x = e.x + ((this.frand(i + 9) + t * 0.03 * (1 + this.frand(i))) % 1) * e.w;
      const tw = 0.5 + 0.5 * Math.sin(t * 4 + ph * 6.283);
      if (tw < 0.45) continue;
      const len = (2 + this.frand(i + 4) * 4) * e.intensity;
      g.rect(x, y, len, 1).fill({ color: col, alpha: tw * 0.7 });
      if (tw > 0.85) g.circle(x + len / 2, y, 1).fill({ color: 0xffffff, alpha: ((tw - 0.85) / 0.15) * 0.8 });
    }
  }
  private fxMotes(g: Graphics, e: Fx, t: number): void {
    const col = e.color ?? 0xffe6b0;
    for (let i = 0; i < e.count; i++) {
      const ph = this.frand(i + e.seed * 3) * 6.283;
      const x = e.x + this.frand(i + 1) * e.w + Math.sin(t * 0.5 + ph) * 6;
      const y = e.y + this.frand(i + 2) * e.h + Math.cos(t * 0.37 + ph * 1.3) * 5 + Math.sin(t * 0.13 + ph) * 4;
      const a = (0.22 + 0.24 * Math.sin(t * 1.3 + ph)) * e.intensity;
      if (a <= 0) continue;
      g.circle(x, y, 0.7).fill({ color: col, alpha: a });
    }
  }
  private fxClouds(g: Graphics, e: Fx, t: number): void {
    const col = e.color ?? 0xffffff;
    const n = Math.max(2, e.count);
    for (let i = 0; i < n; i++) {
      const speed = 0.004 + 0.004 * this.frand(i + 1);
      const x = e.x + (((this.frand(i + 2) + t * speed) % 1.2) - 0.1) * e.w;
      const y = e.y + this.frand(i + 3) * e.h;
      const w = e.w * 0.18 * (0.6 + this.frand(i + 4) * 0.8);
      const h = w * 0.4;
      const a = 0.1 * e.intensity;
      g.ellipse(x, y, w, h).fill({ color: col, alpha: a });
      g.ellipse(x - w * 0.5, y + h * 0.2, w * 0.6, h * 0.7).fill({ color: col, alpha: a });
      g.ellipse(x + w * 0.5, y + h * 0.2, w * 0.6, h * 0.7).fill({ color: col, alpha: a });
    }
  }
  private fxBirds(g: Graphics, e: Fx, t: number): void {
    const col = e.color ?? 0x20202c;
    const n = Math.max(1, e.count);
    for (let i = 0; i < n; i++) {
      const ph = this.frand(i + e.seed * 6);
      const speed = 0.02 + 0.015 * this.frand(i + 1);
      const prog = ((this.frand(i + 2) + t * speed) % 1.2) - 0.1;
      const x = e.x + prog * e.w;
      const y = e.y + (this.frand(i + 3) * 0.5 + 0.1) * e.h + Math.sin(t * 0.6 + ph * 6.283) * e.h * 0.05;
      const flap = Math.sin(t * 8 + ph * 6.283) * 2.2 + 2.4;
      const s = (1 + this.frand(i + 4)) * e.intensity;
      g.moveTo(x - 3 * s, y + flap * 0.2).lineTo(x, y - flap * 0.3).lineTo(x + 3 * s, y + flap * 0.2)
        .stroke({ width: 1, color: col, alpha: 0.7 });
    }
  }

  /** Wordless speech bubble: a mood-tinted rounded panel of pictographic glyphs with a
   * nub pointing at the speaker, plus a "more" cue when further lines follow. */
  drawDialog(d: Dialog | null, timeMs: number): void {
    const g = this.dialog;
    g.clear();
    if (!d) return;
    const ink = 0x2a2418;
    const tint = MOOD_TINT[d.mood] ?? MOOD_TINT.neutral!;
    const cell = 13;
    const padX = 6;
    const padY = 5;
    const w = d.glyphs.length * cell + padX * 2;
    const h = cell + padY * 2;
    const bx = d.x - w / 2;
    const by = d.y - h - 8; // float above the head
    // panel + nub
    g.roundRect(bx, by, w, h, 4).fill({ color: tint });
    g.roundRect(bx, by, w, h, 4).stroke({ width: 1, color: ink, alpha: 0.7 });
    g.poly([d.x - 4, by + h - 0.5, d.x + 4, by + h - 0.5, d.x, by + h + 5]).fill({ color: tint });
    // glyph row
    for (let i = 0; i < d.glyphs.length; i++) {
      const gx = bx + padX + cell * i + cell / 2;
      const gy = by + h / 2;
      this.drawGlyph(g, gx, gy, d.glyphs[i]!, ink);
    }
    // "more" cue: a soft blinking down-chevron at the corner
    if (d.more) {
      const a = 0.4 + 0.4 * Math.sin(timeMs / 250);
      g.poly([bx + w - 7, by + h - 4, bx + w - 3, by + h - 4, bx + w - 5, by + h - 1]).fill({ color: ink, alpha: a });
    }
  }

  /** The pictographic atlas — each glyph drawn in a ~10px box centred at (x,y). Procedural
   * so it stays pixel-crisp, deterministic, and self-contained (no font / external image).
   * Unknown names fall back to a neutral dot; the vocabulary can grow here. */
  private drawGlyph(g: Graphics, x: number, y: number, name: string, ink: number): void {
    const c = { color: ink };
    const line = (pts: number[], w = 1.3) => g.poly(pts).stroke({ width: w, color: ink });
    switch (name) {
      case "greet": // a waving upward arc
        g.moveTo(x - 4, y + 2).quadraticCurveTo(x, y - 6, x + 4, y + 2).stroke({ width: 1.3, color: ink }); break;
      case "bye": // arc + parting dot
        g.moveTo(x - 4, y + 1).quadraticCurveTo(x, y - 5, x + 4, y + 1).stroke({ width: 1.3, color: ink });
        g.circle(x + 5, y - 4, 1).fill(c); break;
      case "question":
        g.moveTo(x - 2.5, y - 3).quadraticCurveTo(x + 3, y - 5, x, y).lineTo(x, y + 1.5).stroke({ width: 1.3, color: ink });
        g.circle(x, y + 4, 1).fill(c); break;
      case "yes": // check
        line([x - 4, y, x - 1, y + 4, x + 4, y - 4], 1.5); break;
      case "no": // cross
        line([x - 4, y - 4, x + 4, y + 4], 1.5); line([x + 4, y - 4, x - 4, y + 4], 1.5); break;
      case "help": // ring (call for aid)
        g.circle(x, y, 4).stroke({ width: 1.3, color: ink }); g.circle(x, y, 1).fill(c); break;
      case "thanks": // a small bow (downward arc)
        g.moveTo(x - 4, y - 2).quadraticCurveTo(x, y + 5, x + 4, y - 2).stroke({ width: 1.3, color: ink }); break;
      case "light": // sun
        g.circle(x, y, 2.5).fill(c);
        for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; line([x + Math.cos(a) * 4, y + Math.sin(a) * 4, x + Math.cos(a) * 5.5, y + Math.sin(a) * 5.5], 1); }
        break;
      case "dark": // crescent
        g.circle(x, y, 4.5).fill(c); g.circle(x + 2, y - 1, 4).fill({ color: 0xe8dcc0 }); break;
      case "key":
        g.circle(x - 2, y, 2.5).stroke({ width: 1.3, color: ink }); line([x, y, x + 5, y], 1.3); line([x + 5, y, x + 5, y + 2.5], 1.3); line([x + 3, y, x + 3, y + 2], 1.3); break;
      case "door":
        g.rect(x - 3.5, y - 5, 7, 10).stroke({ width: 1.3, color: ink }); g.circle(x + 1.5, y, 0.9).fill(c); break;
      case "path": // footsteps / dotted way
        g.circle(x - 3, y + 2, 1).fill(c); g.circle(x, y - 1, 1).fill(c); g.circle(x + 3, y - 4, 1).fill(c); break;
      case "up": g.poly([x, y - 5, x + 4, y + 3, x - 4, y + 3]).fill(c); break;
      case "down": g.poly([x, y + 5, x + 4, y - 3, x - 4, y - 3]).fill(c); break;
      case "home": // house
        g.poly([x - 4, y - 1, x, y - 5, x + 4, y - 1]).fill(c); g.rect(x - 3, y - 1, 6, 5).fill(c); break;
      case "far": // distant peak + dots
        g.poly([x - 1, y + 4, x + 3, y - 4, x + 6, y + 4]).fill(c); g.circle(x - 4, y + 3, 0.8).fill(c); g.circle(x - 6, y + 4, 0.8).fill(c); break;
      case "heart":
        g.circle(x - 2, y - 1, 2).fill(c); g.circle(x + 2, y - 1, 2).fill(c); g.poly([x - 3.6, y, x + 3.6, y, x, y + 4.5]).fill(c); break;
      case "danger": // warning triangle + bang
        g.poly([x, y - 5, x + 5, y + 4, x - 5, y + 4]).stroke({ width: 1.3, color: ink }); line([x, y - 1, x, y + 1.5], 1.3); g.circle(x, y + 3, 0.8).fill(c); break;
      case "water": // wave
        g.moveTo(x - 5, y).quadraticCurveTo(x - 2.5, y - 4, x, y).quadraticCurveTo(x + 2.5, y + 4, x + 5, y).stroke({ width: 1.3, color: ink }); break;
      case "gift": // box + ribbon
        g.rect(x - 4, y - 2, 8, 6).stroke({ width: 1.2, color: ink }); line([x, y - 2, x, y + 4], 1.2); line([x - 4, y - 2, x + 4, y - 2], 1.2); break;
      case "wait": // hourglass
        g.poly([x - 3, y - 4, x + 3, y - 4, x - 3, y + 4, x + 3, y + 4]).stroke({ width: 1.2, color: ink }); break;
      case "follow": // arrow + trailing dot
        line([x - 4, y, x + 3, y], 1.3); g.poly([x + 5, y, x + 1, y - 2.5, x + 1, y + 2.5]).fill(c); g.circle(x - 5.5, y, 0.9).fill(c); break;
      case "star":
        g.poly([x, y - 5, x + 1.4, y - 1.4, x + 5, y, x + 1.4, y + 1.4, x, y + 5, x - 1.4, y + 1.4, x - 5, y, x - 1.4, y - 1.4]).fill(c); break;
      default:
        g.circle(x, y, 1.5).fill(c);
    }
  }

  /** Build a room's static visuals into the world, offset in room-space units. */
  buildRoom(room: Room, offset: Vec2 = { x: 0, y: 0 }): RoomHandle {
    const bg = new Graphics();
    bg.rect(offset.x, offset.y, room.size.w, room.size.h).fill({ color: room.background.color });
    for (const poly of room.walkmesh) bg.poly(flatOff(poly, offset));
    bg.fill({ color: 0x1d1d2a }); // floor tint
    bg.zIndex = -1_000_000;
    this.world.addChild(bg);

    // decor: purely-visual labeled silhouettes composing the backdrop/dressing. One layer,
    // ordered by `z`, sitting above the floor but BEHIND all actors (walk-behind props use
    // occluders instead). Greybox renders them as flat colored shapes; later the art-swap pass
    // replaces them with painted sprites keyed by the same ids/kinds.
    const decor = new Graphics();
    for (const d of [...room.decor].sort((a, b) => a.z - b.z)) {
      addShape(decor, d.shape, offset);
      decor.fill({ color: d.color, alpha: d.alpha });
    }
    decor.zIndex = -800_000;
    this.world.addChild(decor);

    // painted backdrop (art-swap): a sprite filling the room behind the floor + actors. When set,
    // it replaces the greybox — hide the decor silhouettes (the painting carries the detail now).
    const fireUnits: FireUnit[] = []; // filled once the art loads (shader fire over the painted fire)
    let bgSprite: Sprite | null = null;
    if (room.bgImage) {
      bgSprite = new Sprite();
      bgSprite.x = offset.x;
      bgSprite.y = offset.y;
      bgSprite.zIndex = -900_000; // above the bg-colour/floor fill, below decor/actors
      this.world.addChild(bgSprite);
      decor.visible = false;
      // load async, then size to the room (avoids the Sprite.from 1x1-texture mis-scale)
      const sp = bgSprite;
      Assets.load(room.bgImage)
        .then((tex) => {
          if (sp.destroyed) return; // room switched before the texture finished loading
          tex.source.scaleMode = "nearest"; // crisp chunky pixels (true pixel-art look)
          sp.texture = tex;
          // scale to room size (scale.set avoids Pixi v8's width-setter trim path)
          sp.scale.set(room.size.w / tex.width, room.size.h / tex.height);
        })
        .catch((e) => console.error("[bg] failed to load", room.bgImage, e));
      this.loadFireCutout(room, offset, fireUnits); // animate the fire via its baked cutout, if any
    }

    // gated areas: floor fill drawn just above the bg, shown only while "open"
    const gatedAreaNodes = new Map<string, Graphics>();
    for (const area of room.gatedAreas) {
      const g = new Graphics();
      g.poly(flatOff(area.polygon, offset)).fill({ color: 0x24304a }); // a touch brighter than floor
      g.poly(flatOff(area.polygon, offset)).stroke({ width: 1, color: 0x3d5a8a, alpha: 0.6 });
      g.zIndex = -999_000;
      g.visible = false; // main toggles per-frame from room state
      this.world.addChild(g);
      gatedAreaNodes.set(area.id, g);
    }

    const occluderNodes = new Map<string, Container>();
    for (const occ of room.occluders) {
      const g = new Graphics();
      g.rect(offset.x + occ.x, offset.y + occ.y, occ.w, occ.h).fill({ color: occ.color });
      g.rect(offset.x + occ.x, offset.y + occ.y, occ.w, occ.h).stroke({ width: 1, color: 0x000000, alpha: 0.4 });
      this.world.addChild(g);
      occluderNodes.set(occ.id, g);
    }

    const npcNodes = new Map<string, Container>();
    for (const npc of room.npcs) {
      const c = new Container();
      c.x = offset.x + npc.x;
      c.y = offset.y + npc.y;
      drawFigure(c, npc.color, false);
      this.world.addChild(c);
      npcNodes.set(npc.id, c);
    }

    return { room, offset, bg, decor, bgSprite, occluderNodes, npcNodes, gatedAreaNodes, fireUnits };
  }

  /** Load the room's baked fire CUTOUT (art/<room>_fire.png — art colour + a fire-shaped alpha mask,
   * authored, see tools/gen_firemask) and twinkle it over the bg. Because the cutout is fire-shaped,
   * the animation can never warp stone and has no rectangular footprint. No-op if there's no cutout. */
  private loadFireCutout(room: Room, offset: Vec2, out: FireUnit[]): void {
    if (!room.bgImage) return;
    const path = room.bgImage.replace(/\.png$/i, "_fire.png");
    const seed = room.fx.find((e) => e.kind === "flames")?.seed ?? 1;
    const sprite = new Sprite();
    sprite.x = offset.x;
    sprite.y = offset.y;
    sprite.zIndex = -890_000; // above the painted backdrop, below actors
    this.world.addChild(sprite);
    const unit: FireUnit = { sprite, filter: null };
    out.push(unit);
    Assets.load(path)
      .then((tex) => {
        if (sprite.destroyed) return;
        tex.source.scaleMode = "nearest";
        sprite.texture = tex;
        sprite.scale.set(room.size.w / tex.width, room.size.h / tex.height);
        unit.filter = createFireFilter(seed, tex.width, tex.height);
        sprite.filters = [unit.filter];
      })
      .catch(() => { if (!sprite.destroyed) sprite.destroy(); }); // no cutout for this room — fine
  }

  /** Advance every fire filter in the current room (call once per frame). */
  updateFire(h: RoomHandle, timeMs: number): void {
    const s = timeMs / 1000;
    for (const u of h.fireUnits) u.filter?.setTime(s);
  }

  /** Toggle which gated areas are shown as open floor (driven by room state). */
  setOpenAreas(h: RoomHandle, openIds: Set<string>): void {
    for (const [id, node] of h.gatedAreaNodes) node.visible = openIds.has(id);
  }

  destroyRoom(h: RoomHandle): void {
    h.bg.destroy();
    h.decor.destroy();
    h.bgSprite?.destroy();
    for (const u of h.fireUnits) { if (!u.sprite.destroyed) u.sprite.destroy(); u.filter?.destroy(); }
    for (const g of h.occluderNodes.values()) g.destroy();
    for (const c of h.npcNodes.values()) c.destroy();
    for (const g of h.gatedAreaNodes.values()) g.destroy();
  }

  /** Camera in room-space units (top-left of view). */
  setCamera(x: number, y: number): void {
    this.world.x = this.offX - x * this.scale;
    this.world.y = this.offY - y * this.scale;
  }

  setHeroWorld(x: number, y: number): void {
    this.hero.x = x;
    this.hero.y = y;
  }

  // ---- directional pixel-art hero sprite (replaces the greybox figure once loaded) ----
  /** Load Brand's 8 idle rotations + optional walk frames from a base path (e.g. "sprites/brand").
   * `walk` maps dir -> ordered frame paths. Textures render nearest-neighbour (crisp pixels). */
  async loadHeroSprites(base: string, walk?: Record<string, string[]>): Promise<void> {
    const dirs = ["south", "north", "east", "west", "south-east", "north-east", "north-west", "south-west"];
    // idle rotations first, so the sprite shows ASAP (each guarded — one miss won't block the rest)
    await Promise.all(
      dirs.map(async (d) => {
        try {
          const tex = await Assets.load(`${base}/${d}.png`);
          tex.source.scaleMode = "nearest";
          this.heroIdle.set(d, tex);
        } catch { /* missing direction — fall back to a near one at pose time */ }
      }),
    );
    this.heroReady = this.heroIdle.size > 0;
    // walk frames load after (guarded per-direction; a failure just leaves that dir on idle)
    if (walk) {
      await Promise.all(
        dirs.map(async (d) => {
          if (!walk[d]) return;
          try {
            const frames = await Promise.all(walk[d]!.map((p) => Assets.load(p)));
            frames.forEach((t) => (t.source.scaleMode = "nearest"));
            this.heroWalk.set(d, frames);
          } catch { /* this direction stays on its idle frame */ }
        }),
      );
    }
  }

  /** Pose the hero sprite from a movement vector + moving flag (8-way direction; walk-cycles when
   * moving). HERO_H sets the on-screen height; HERO_FOOT lifts the sprite so its feet sit on (0,0). */
  setHeroPose(dx: number, dy: number, moving: boolean, timeMs: number): void {
    if (!this.heroReady) return;
    if (moving && (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01)) this.heroDir = dir8(dx, dy);
    const dir = this.nearestHeroDir(this.heroDir);
    const frames = this.heroWalk.get(dir);
    let tex = this.heroIdle.get(dir);
    if (moving && frames && frames.length) tex = frames[Math.floor(timeMs / 110) % frames.length];
    if (!tex) return;
    // sprites are trimmed to the character bbox (feet at the image bottom), so anchor (0.5,1) puts
    // feet exactly on the container origin = actor.pos. HERO_H is the on-screen feet-to-hood height.
    const HERO_H = 62, HERO_FOOT = 0;
    this.heroSprite.texture = tex;
    this.heroSprite.height = HERO_H;
    this.heroSprite.width = (tex.width / tex.height) * HERO_H;
    this.heroSprite.y = HERO_FOOT;
    this.heroSprite.visible = true;
    if (this.heroFallback) this.heroFallback.visible = false;
  }
  private nearestHeroDir(d: string): string {
    if (this.heroIdle.has(d)) return d;
    const order = ["south", "south-east", "east", "north-east", "north", "north-west", "west", "south-west"];
    const i = order.indexOf(d);
    for (let r = 1; r < 8; r++) {
      const a = this.heroIdle.get(order[(i + r) % 8]!) ? order[(i + r) % 8]! : null;
      if (a) return a;
    }
    return d;
  }
  setHeroZ(z: number): void {
    this.hero.zIndex = z;
  }

  setFade(alpha: number): void {
    this.fade.clear();
    if (alpha > 0) this.fade.rect(0, 0, this.viewW, this.viewH).fill({ color: 0x000000, alpha });
  }

  toggleDebug(): void {
    this.showDebug = !this.showDebug;
    this.debug.visible = this.showDebug;
  }

  /** Draw debug for the given room handles (each at its own offset). */
  drawDebug(handles: RoomHandle[], info: DebugInfo): void {
    const d = this.debug;
    d.clear();
    if (!this.showDebug) return;

    for (const h of handles) {
      const o = h.offset;
      for (const poly of h.room.walkmesh) {
        d.poly(flatOff(poly, o)).fill({ color: 0x2e7d32, alpha: 0.08 });
        d.poly(flatOff(poly, o)).stroke({ width: 1, color: 0x43a047, alpha: 0.9 });
      }
      for (const hs of h.room.hotspots) d.poly(flatOff(hs.polygon, o)).stroke({ width: 1, color: 0x00bcd4, alpha: 0.9 });
      for (const occ of h.room.occluders) {
        d.moveTo(o.x + occ.x, o.y + occ.baselineY).lineTo(o.x + occ.x + occ.w, o.y + occ.baselineY);
        d.stroke({ width: 1, color: 0xffd54f, alpha: 0.85 });
      }
      for (const ex of h.room.exits) d.poly(flatOff(ex.polygon, o)).stroke({ width: 1, color: 0xab47bc, alpha: 0.9 });
      for (const area of h.room.gatedAreas) d.poly(flatOff(area.polygon, o)).stroke({ width: 1, color: 0xff9100, alpha: 0.8 });
      for (const npc of h.room.npcs) {
        const node = h.npcNodes.get(npc.id);
        const cx = node ? node.x : o.x + npc.x;
        const cy = node ? node.y : o.y + npc.y;
        d.circle(cx, cy, npc.radius).stroke({ width: 1, color: 0xff9800, alpha: 0.7 });
      }
    }

    if (info.path.length) {
      d.moveTo(this.hero.x, this.hero.y);
      for (const p of info.path) d.lineTo(p.x, p.y);
      d.stroke({ width: 1, color: 0xff4081, alpha: 0.9 });
      for (const p of info.path) d.circle(p.x, p.y, 1.6).fill({ color: 0xff4081 });
    }
    if (info.target) {
      d.circle(info.target.x, info.target.y, 3).stroke({ width: 1, color: info.clamped ? 0xff5252 : 0x69f0ae });
    }
    if (info.lastClick) d.circle(info.lastClick.x, info.lastClick.y, 1.5).fill({ color: 0xffffff });
  }
}
